import { useEffect, useMemo, useState } from "react";

import { useCollectionsList, useConfiguration, useWidget } from "@is/hooks";
import { WidgetItem, WidgetType } from "@is/types";
import { createWidgetItem } from "@is/utils";

export const useManageEntryWidgets = () => {
  const [collectionToDisplay, setCollectionToDisplay] = useState(null);
  const [widget, setWidget] = useState<WidgetItem | null>(null);
  const [isWidgetPending, setIsWidgetPending] = useState<boolean>(false);

  const { getDemoCollection } = useCollectionsList({
    filterOutOfStockItems: true,
  });

  const { attributes, backgroundMedia } = useConfiguration({
    revalidateIfStale: true,
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    shouldRetryOnError: true,
  });

  const isDiscoverYourOutfitsEnabled = useMemo(
    () => backgroundMedia?.discoverYourOutfits ?? false,
    [backgroundMedia]
  );

  const [, , getHTWIWidget] = useWidget();

  useEffect(() => {
    const fetchDemoCollectionItems = async () => {
      try {
        const demoCollection = await getDemoCollection();

        if (demoCollection?.items) {
          const itemsInStock = demoCollection.items
            .filter((item) =>
              item.hasOnlineStock || item.hasInstoreStock ? true : false
            )
            .map((item) => ({
              variantId: item.variantId,
              styleId: item.styleId,
            }));

          setCollectionToDisplay(itemsInStock);
        } else {
          setCollectionToDisplay([]);
        }
      } catch (error) {
        console.warn("Demo collection not found or error occurred:", error);
        setCollectionToDisplay([]);
      }
    };

    if (isDiscoverYourOutfitsEnabled) {
      fetchDemoCollectionItems();
    } else {
      setCollectionToDisplay(null);
    }
  }, [isDiscoverYourOutfitsEnabled, getDemoCollection]);

  useEffect(() => {
    const fetchWidgets = async () => {
      if (
        !collectionToDisplay ||
        !attributes ||
        !isDiscoverYourOutfitsEnabled
      ) {
        if (!isDiscoverYourOutfitsEnabled) {
          setWidget(null);
          setIsWidgetPending(false);
        }
        return;
      }

      if (collectionToDisplay.length === 0) {
        setWidget(null);
        setIsWidgetPending(false);
        return;
      }

      setIsWidgetPending(true);
      setWidget(null);

      const primaryAttributes = attributes.filter((attr) => attr.primary);

      const filters = primaryAttributes
        .filter((attr) => attr.term)
        .map((attr) => ({
          key: attr.key,
          value: attr.term.value,
          type: attr.term.type,
          label: attr.term.label,
          isPrimary: true,
        }));

      const allHowToWearItItems = [];

      const widgetPromises = collectionToDisplay.map(async (item) => {
        try {
          const howToWearItWidgets = (
            await getHTWIWidget(item.variantId, item.styleId, filters, false)
          )?.filter((widget) => widget.modelShotUrl);

          return howToWearItWidgets?.length ? howToWearItWidgets : [];
        } catch (error) {
          console.error(
            `Failed to fetch HTWI widget for style ${item.styleId}:`,
            error
          );
          return [];
        }
      });

      const results = await Promise.allSettled(widgetPromises);

      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value.length > 0) {
          allHowToWearItItems.push(...result.value);
        }
      });

      if (allHowToWearItItems.length > 0) {
        const singleWidget = createWidgetItem({
          type: WidgetType.HowToWearIt,
          items: allHowToWearItItems,
        });

        setWidget(singleWidget);
      }

      setIsWidgetPending(false);
    };

    fetchWidgets();
  }, [
    isDiscoverYourOutfitsEnabled,
    collectionToDisplay,
    attributes,
    getHTWIWidget,
  ]);

  return {
    widget,
    isWidgetPending,
  };
};
